import { jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Field, Root, createInputScope } from "@codefast-ui/input";
import { composeEventHandlers } from "@radix-ui/primitive";
import { createContextScope } from "@radix-ui/react-context";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
const NUMBER_INPUT_NAME = "InputNumber";
const [createInputNumberContext, createInputNumberScope] = createContextScope(NUMBER_INPUT_NAME, [
    createInputScope
]);
const useInputScope = createInputScope();
const [InputNumberContextProvider, useInputNumberContext] = createInputNumberContext(NUMBER_INPUT_NAME);
function InputNumber(numberInputProps) {
    const { __scopeInputNumber, ariaDecrementLabel, ariaIncrementLabel, defaultValue, formatOptions = {
        minimumFractionDigits: 0,
        style: "decimal"
    }, id, locale, max, min, onChange, step = 1, value: valueProperty, ...props } = numberInputProps;
    const inputScope = useInputScope(__scopeInputNumber);
    const inputRef = useRef(null);
    const [value, setValue] = useControllableState({
        defaultProp: defaultValue,
        onChange,
        prop: valueProperty
    });
    const { decimalSeparator, thousandSeparator } = useMemo(()=>getNumberFormatSeparators(locale), [
        locale
    ]);
    const formatValue = useCallback((inputValue)=>{
        if (void 0 === inputValue || Number.isNaN(inputValue)) return "";
        return new Intl.NumberFormat(locale, formatOptions).format(inputValue);
    }, [
        formatOptions,
        locale
    ]);
    const parseValue = useCallback((inputValue)=>{
        if ("number" == typeof inputValue) return clamp(inputValue, min, max);
        if ("string" != typeof inputValue) return NaN;
        const cleanedValue = inputValue.trim().replaceAll(/[^\d.,\-()]/g, "");
        if ("" === cleanedValue) return NaN;
        const normalizedValue = normalizeInputValue(cleanedValue, thousandSeparator, decimalSeparator);
        let parsedValue = Number.parseFloat(normalizedValue);
        if ("percent" === formatOptions.style) parsedValue /= 100;
        return Number.isNaN(parsedValue) ? 0 : clamp(parsedValue, min, max);
    }, [
        decimalSeparator,
        formatOptions.style,
        max,
        min,
        thousandSeparator
    ]);
    const changeNumberValue = useCallback((operation)=>{
        const inputElement = inputRef.current;
        if (!inputElement || props.disabled || props.readOnly) return;
        const currentValue = parseValue(inputElement.value) || 0;
        const newValue = clamp(operation(currentValue), min, max);
        inputElement.value = formatValue(newValue);
        setValue(newValue);
    }, [
        props.disabled,
        formatValue,
        max,
        min,
        parseValue,
        props.readOnly,
        setValue
    ]);
    const handleIncrement = useCallback(()=>{
        changeNumberValue((number)=>number + step);
    }, [
        changeNumberValue,
        step
    ]);
    const handleDecrement = useCallback(()=>{
        changeNumberValue((number)=>number - step);
    }, [
        changeNumberValue,
        step
    ]);
    const handleIncrementToMax = useCallback(()=>{
        changeNumberValue((number)=>max ?? number + step);
    }, [
        changeNumberValue,
        max,
        step
    ]);
    const handleDecrementToMin = useCallback(()=>{
        changeNumberValue((number)=>min ?? number - step);
    }, [
        changeNumberValue,
        min,
        step
    ]);
    return /*#__PURE__*/ jsx(InputNumberContextProvider, {
        ariaDecrementLabel: ariaDecrementLabel,
        ariaIncrementLabel: ariaIncrementLabel,
        defaultValue: defaultValue,
        disabled: props.disabled,
        formatOptions: formatOptions,
        formatValue: formatValue,
        id: id,
        inputRef: inputRef,
        max: max,
        min: min,
        parseValue: parseValue,
        readOnly: props.readOnly,
        scope: __scopeInputNumber,
        value: value,
        onChange: setValue,
        onDecrement: handleDecrement,
        onDecrementToMin: handleDecrementToMin,
        onIncrement: handleIncrement,
        onIncrementToMax: handleIncrementToMax,
        children: /*#__PURE__*/ jsx(Root, {
            ...inputScope,
            ...props
        })
    });
}
const NUMBER_INPUT_FIELD_NAME = "InputNumberField";
function InputNumberField({ __scopeInputNumber, onBlur, onKeyDown, ...props }) {
    const inputScope = useInputScope(__scopeInputNumber);
    const { defaultValue, disabled, formatValue, id, inputRef, max, min, onChange, onDecrement, onDecrementToMin, onIncrement, onIncrementToMax, parseValue, readOnly, step, value } = useInputNumberContext(NUMBER_INPUT_FIELD_NAME, __scopeInputNumber);
    const handleBlur = useCallback((event)=>{
        const numericValue = parseValue(event.target.value);
        const formattedValue = formatValue(numericValue);
        if (formattedValue !== event.target.value) event.target.value = formattedValue;
        onChange(numericValue);
    }, [
        formatValue,
        onChange,
        parseValue
    ]);
    const handleKeyDown = useCallback((event)=>{
        switch(event.key){
            case "ArrowUp":
                onIncrement();
                event.preventDefault();
                break;
            case "PageUp":
                onIncrementToMax();
                event.preventDefault();
                break;
            case "ArrowDown":
                onDecrement();
                event.preventDefault();
                break;
            case "PageDown":
                onDecrementToMin();
                event.preventDefault();
                break;
            default:
                break;
        }
    }, [
        onIncrement,
        onIncrementToMax,
        onDecrement,
        onDecrementToMin
    ]);
    const handleKeyDownPrevent = useCallback((event)=>{
        switch(event.key){
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowLeft":
            case "ArrowRight":
            case "PageUp":
            case "PageDown":
            case "Tab":
            case "Escape":
            case "Enter":
            case "Backspace":
            case "Delete":
            case "Home":
            case "End":
            case ".":
            case ",":
            case "-":
            case "%":
                return;
            default:
                if (isNumberKey(event.key) || isModifierKey(event) || isFunctionKey(event.key)) return;
                event.preventDefault();
        }
    }, []);
    const handleKeyDownEnter = useCallback((event)=>{
        const inputElement = inputRef.current;
        if ("Enter" !== event.key || !inputElement) return;
        const numericValue = parseValue(inputElement.value);
        const formattedValue = formatValue(numericValue);
        if (formattedValue !== inputElement.value) inputElement.value = formattedValue;
        onChange(numericValue);
    }, [
        formatValue,
        inputRef,
        onChange,
        parseValue
    ]);
    const combinedKeyDownHandler = useCallback(()=>composeEventHandlers(onKeyDown, chain(handleKeyDownPrevent, handleKeyDown, handleKeyDownEnter)), [
        onKeyDown,
        handleKeyDown,
        handleKeyDownEnter,
        handleKeyDownPrevent
    ]);
    useEffect(()=>{
        const handleWheel = (event)=>{
            const inputElement = inputRef.current;
            if (!inputElement || disabled || readOnly || document.activeElement !== inputElement) return;
            event.preventDefault();
            if (event.deltaY > 0) onIncrement();
            else onDecrement();
        };
        const inputElement = inputRef.current;
        inputElement?.addEventListener("wheel", handleWheel);
        return ()=>{
            inputElement?.removeEventListener("wheel", handleWheel);
        };
    }, [
        onIncrement,
        onDecrement,
        inputRef,
        disabled,
        readOnly
    ]);
    useEffect(()=>{
        const inputElement = inputRef.current;
        if (inputElement && inputElement !== document.activeElement) inputElement.value = formatValue(value);
    }, [
        formatValue,
        inputRef,
        value
    ]);
    useEffect(()=>{
        const inputElement = inputRef.current;
        if (!inputElement) return;
        const handleReset = ()=>{
            onChange(parseValue(defaultValue));
        };
        const form = inputElement.form;
        form?.addEventListener("reset", handleReset);
        return ()=>{
            form?.removeEventListener("reset", handleReset);
        };
    }, [
        defaultValue,
        inputRef,
        onChange,
        parseValue
    ]);
    return /*#__PURE__*/ jsx(Field, {
        ref: inputRef,
        defaultValue: formatValue(value),
        disabled: disabled,
        id: id,
        inputMode: "decimal",
        max: max,
        min: min,
        readOnly: readOnly,
        step: step,
        onBlur: composeEventHandlers(onBlur, handleBlur),
        onKeyDown: combinedKeyDownHandler(),
        ...inputScope,
        ...props
    });
}
const NUMBER_STEPPER_BUTTON_NAME = "NumberStepperButton";
function NumberStepperButton({ __scopeInputNumber, operation, ...props }) {
    const { ariaDecrementLabel, ariaIncrementLabel, disabled, id, max, min, onDecrement, onIncrement, value } = useInputNumberContext(NUMBER_STEPPER_BUTTON_NAME, __scopeInputNumber);
    const isDisabled = useMemo(()=>{
        const atMin = void 0 !== min && void 0 !== value && value <= min;
        const atMax = void 0 !== max && void 0 !== value && value >= max;
        return (disabled ?? atMin) || atMax;
    }, [
        min,
        max,
        value,
        disabled
    ]);
    const timeoutIdRef = useRef(null);
    const startActionInterval = useCallback((callback)=>{
        const interval = 100;
        const repeatAction = ()=>{
            callback();
            timeoutIdRef.current = setTimeout(repeatAction, interval);
        };
        callback();
        timeoutIdRef.current = setTimeout(repeatAction, 2 * interval);
    }, []);
    const clearActionInterval = useCallback(()=>{
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    }, []);
    const handlePointerDown = useCallback(()=>{
        const action = "increment" === operation ? onIncrement : onDecrement;
        startActionInterval(action);
    }, [
        onDecrement,
        onIncrement,
        operation,
        startActionInterval
    ]);
    const handleContextMenu = useCallback((event)=>{
        event.preventDefault();
    }, []);
    const handleKeyDown = useCallback((event)=>{
        if ("Enter" === event.key || " " === event.key) {
            event.preventDefault();
            const action = "increment" === operation ? onIncrement : onDecrement;
            action();
        }
    }, [
        onDecrement,
        onIncrement,
        operation
    ]);
    return /*#__PURE__*/ jsx("button", {
        "aria-controls": id,
        "aria-label": "increment" === operation ? ariaIncrementLabel : ariaDecrementLabel,
        "aria-live": "polite",
        disabled: isDisabled,
        type: "button",
        onContextMenu: handleContextMenu,
        onKeyDown: handleKeyDown,
        onPointerCancel: clearActionInterval,
        onPointerDown: handlePointerDown,
        onPointerLeave: clearActionInterval,
        onPointerUp: clearActionInterval,
        ...props
    });
}
function InputNumberIncrementButton(props) {
    return /*#__PURE__*/ jsx(NumberStepperButton, {
        operation: "increment",
        ...props
    });
}
function InputNumberDecrementButton(props) {
    return /*#__PURE__*/ jsx(NumberStepperButton, {
        operation: "decrement",
        ...props
    });
}
function chain(...callbacks) {
    return (...args)=>{
        for (const callback of callbacks)callback(...args);
    };
}
function getNumberFormatSeparators(locale) {
    const numberFormat = new Intl.NumberFormat(locale);
    const parts = numberFormat.formatToParts(12345.6);
    let thousandSeparator = "";
    let decimalSeparator = "";
    for (const part of parts){
        if ("group" === part.type) thousandSeparator = part.value;
        if ("decimal" === part.type) decimalSeparator = part.value;
        if (thousandSeparator && decimalSeparator) break;
    }
    return {
        decimalSeparator,
        thousandSeparator
    };
}
function normalizeInputValue(value, thousandSeparator, decimalSeparator) {
    return value.replaceAll(new RegExp(`\\${thousandSeparator}`, "g"), "").replace(new RegExp(`\\${decimalSeparator}`), ".").replaceAll(/[()]/g, "-");
}
function isModifierKey(event) {
    return event.ctrlKey || event.altKey || event.metaKey || event.shiftKey;
}
function isFunctionKey(key) {
    return key.startsWith("F") && key.length > 1;
}
function isNumberKey(key) {
    return !Number.isNaN(Number(key));
}
function clamp(value, min = -1 / 0, max = 1 / 0) {
    return Math.min(Math.max(value, min), max);
}
export { InputNumberDecrementButton as DecrementButton, InputNumberField as Field, InputNumberIncrementButton as IncrementButton, InputNumber, InputNumberDecrementButton, InputNumberField, InputNumberIncrementButton, InputNumber as Root, createInputNumberScope };
